import { useCallback, useEffect, useRef, useState } from 'react'
import { listAuditLogs } from '@/api/audit'
import { listUsers } from '@/api/users'
import Pagination from '@/components/Pagination'
import { formatKstDateTime } from '@/utils/datetime'
import type { ApiError, AuditLog, AuditResult, ManagedUser } from '@/types'

const PAGE_SIZE = 10

function kstDayBoundary(date: string, endOfDay: boolean): string | undefined {
  if (!date) return undefined
  const time = endOfDay ? '23:59:59.999' : '00:00:00.000'
  return new Date(`${date}T${time}+09:00`).toISOString()
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<AuditLog | null>(null)

  const [actorUserId, setActorUserId] = useState('')
  const [action, setAction] = useState('')
  const [targetType, setTargetType] = useState('')
  const [targetId, setTargetId] = useState('')
  const [result, setResult] = useState<AuditResult | ''>('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const requestSequence = useRef(0)

  const loadLogs = useCallback(async () => {
    const requestId = ++requestSequence.current
    setLoading(true)
    try {
      const response = await listAuditLogs({
        actor_user_id: actorUserId || undefined,
        action: action.trim() || undefined,
        target_type: targetType.trim() || undefined,
        target_id: targetId.trim() || undefined,
        result: result || undefined,
        created_from: kstDayBoundary(createdFrom, false),
        created_to: kstDayBoundary(createdTo, true),
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      if (requestId !== requestSequence.current) return
      setLogs(response.items)
      setTotal(response.total)
      setError(null)
    } catch (caught: unknown) {
      if (requestId !== requestSequence.current) return
      setError((caught as ApiError)?.message ?? 'Failed to load audit logs.')
    } finally {
      if (requestId === requestSequence.current) setLoading(false)
    }
  }, [action, actorUserId, createdFrom, createdTo, page, result, targetId, targetType])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  useEffect(() => {
    listUsers({ limit: 1000, offset: 0 })
      .then((response) => setUsers(response.items))
      .catch(() => {})
  }, [])

  function resetFilters() {
    setActorUserId('')
    setAction('')
    setTargetType('')
    setTargetId('')
    setResult('')
    setCreatedFrom('')
    setCreatedTo('')
    setPage(1)
  }

  function changeFilter(setter: (value: string) => void, value: string) {
    setter(value)
    setPage(1)
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 5px', fontSize: 22 }}>Audit Logs</h1>
      <p style={{ margin: '0 0 20px', color: '#777', fontSize: 13 }}>
        Account, security, and operator actions. Times are displayed in KST.
      </p>

      <div style={filterCardStyle}>
        <select
          aria-label="Actor user"
          value={actorUserId}
          onChange={(event) => changeFilter(setActorUserId, event.target.value)}
          style={controlStyle}
        >
          <option value="">All users</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.display_name || user.username} ({user.username})
            </option>
          ))}
        </select>
        <input
          aria-label="Action"
          placeholder="action"
          value={action}
          onChange={(event) => changeFilter(setAction, event.target.value)}
          style={controlStyle}
        />
        <input
          aria-label="Target type"
          placeholder="target type"
          value={targetType}
          onChange={(event) => changeFilter(setTargetType, event.target.value)}
          style={controlStyle}
        />
        <input
          aria-label="Target ID"
          placeholder="target id"
          value={targetId}
          onChange={(event) => changeFilter(setTargetId, event.target.value)}
          style={controlStyle}
        />
        <select
          aria-label="Result"
          value={result}
          onChange={(event) => {
            setResult(event.target.value as AuditResult | '')
            setPage(1)
          }}
          style={controlStyle}
        >
          <option value="">All results</option>
          <option value="success">Success</option>
          <option value="denied">Denied</option>
          <option value="failed">Failed</option>
        </select>
        <label style={dateLabelStyle}>
          From
          <input
            type="date"
            value={createdFrom}
            onChange={(event) => changeFilter(setCreatedFrom, event.target.value)}
            style={controlStyle}
          />
        </label>
        <label style={dateLabelStyle}>
          To
          <input
            type="date"
            value={createdTo}
            onChange={(event) => changeFilter(setCreatedTo, event.target.value)}
            style={controlStyle}
          />
        </label>
        <button type="button" onClick={resetFilters} style={secondaryButtonStyle}>Clear</button>
        <button type="button" onClick={loadLogs} style={secondaryButtonStyle}>Refresh</button>
      </div>

      {error && (
        <div style={errorBannerStyle}>
          {error}
          <button type="button" onClick={loadLogs} style={inlineButtonStyle}>Retry</button>
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Actor</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Target</th>
                <th style={thStyle}>Result</th>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td style={tdStyle}>{formatKstDateTime(log.created_at)}</td>
                  <td style={tdStyle}>
                    <div>{log.actor_username_snapshot ?? log.actor_type}</div>
                    {log.actor_user_id && <div style={monoSecondaryStyle}>{log.actor_user_id}</div>}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{log.action}</td>
                  <td style={tdStyle}>
                    {log.target_type || log.target_id
                      ? `${log.target_type ?? 'target'}${log.target_id ? ` / ${log.target_id}` : ''}`
                      : '—'}
                  </td>
                  <td style={tdStyle}><ResultBadge result={log.result} /></td>
                  <td style={tdStyle}>{log.reason ?? '—'}</td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      aria-label={`View details for ${log.action}`}
                      onClick={() => setSelected(log)}
                      style={secondaryButtonStyle}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={7} style={emptyCellStyle}>No audit logs found.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={7} style={emptyCellStyle}>Loading audit logs...</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading && !error && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            itemLabel="audit records"
            onPageChange={setPage}
          />
        )}
      </div>

      {selected && (
        <AuditDetailsModal log={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}

function ResultBadge({ result }: { result: AuditResult }) {
  const styles: Record<AuditResult, React.CSSProperties> = {
    success: { color: '#2e7d32', background: '#e8f5e9' },
    denied: { color: '#ad5700', background: '#fff3e0' },
    failed: { color: '#c62828', background: '#ffebee' },
  }
  return <span style={{ ...badgeStyle, ...styles[result] }}>{result}</span>
}

function AuditDetailsModal({ log, onClose }: { log: AuditLog; onClose: () => void }) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const target = log.target_type || log.target_id
    ? `${log.target_type ?? 'target'}${log.target_id ? ` / ${log.target_id}` : ''}`
    : null

  return (
    <div
      style={modalBackdropStyle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-details-title"
        style={detailsModalStyle}
      >
        <div style={detailsHeaderStyle}>
          <h2 id="audit-details-title" style={modalTitleStyle}>Audit details</h2>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>Close</button>
        </div>
        <div style={detailsGridStyle}>
          <Detail label="Time" value={formatKstDateTime(log.created_at)} />
          <Detail label="Result" value={log.result} />
          <Detail label="Action" value={log.action} />
          <Detail label="Target" value={target} />
          <Detail label="Actor type" value={log.actor_type} />
          <Detail label="Actor username" value={log.actor_username_snapshot} />
          <Detail label="Actor user ID" value={log.actor_user_id} />
          <Detail label="IP address" value={log.ip_address} />
          <Detail label="Audit ID" value={log.id} />
          <Detail label="Request ID" value={log.request_id} />
          <Detail label="Reason" value={log.reason} />
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={detailLabelStyle}>Details</div>
          <pre style={jsonStyle}>{JSON.stringify(log.details ?? {}, null, 2)}</pre>
        </div>
      </div>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div style={detailLabelStyle}>{label}</div>
      <div style={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{value ?? '—'}</div>
    </div>
  )
}

const filterCardStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8, marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 6, background: '#fff' }
const controlStyle: React.CSSProperties = { minHeight: 34, padding: '6px 9px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontSize: 12, fontFamily: 'inherit' }
const dateLabelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, color: '#666', fontSize: 10, textTransform: 'uppercase' }
const secondaryButtonStyle: React.CSSProperties = { minHeight: 34, padding: '6px 12px', border: '1px solid #ccc', borderRadius: 4, background: '#fff', color: '#444', cursor: 'pointer', fontFamily: 'inherit' }
const inlineButtonStyle: React.CSSProperties = { marginLeft: 12, border: 'none', background: 'transparent', color: '#3949ab', cursor: 'pointer' }
const errorBannerStyle: React.CSSProperties = { marginBottom: 16, padding: '10px 12px', background: '#fdecea', color: '#c62828', borderRadius: 4, fontSize: 13 }
const cardStyle: React.CSSProperties = { padding: 16, border: '1px solid #e0e0e0', borderRadius: 6, background: '#fff' }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12 }
const thStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '2px solid #ddd', color: '#555', textAlign: 'left', whiteSpace: 'nowrap' }
const tdStyle: React.CSSProperties = { padding: '9px 10px', borderBottom: '1px solid #eee', verticalAlign: 'top', maxWidth: 280, overflowWrap: 'anywhere' }
const emptyCellStyle: React.CSSProperties = { ...tdStyle, padding: 28, color: '#888', textAlign: 'center' }
const monoSecondaryStyle: React.CSSProperties = { marginTop: 3, color: '#999', fontFamily: 'monospace', fontSize: 10 }
const badgeStyle: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }
const modalBackdropStyle: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(0,0,0,.45)' }
const detailsModalStyle: React.CSSProperties = { width: '100%', maxWidth: 760, maxHeight: '85vh', overflowY: 'auto', padding: 24, borderRadius: 8, background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,.25)' }
const detailsHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }
const modalTitleStyle: React.CSSProperties = { margin: 0, fontSize: 19 }
const detailsGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, fontSize: 12 }
const detailLabelStyle: React.CSSProperties = { marginBottom: 3, color: '#888', fontSize: 10, textTransform: 'uppercase' }
const jsonStyle: React.CSSProperties = { maxHeight: 320, margin: 0, padding: 12, overflow: 'auto', borderRadius: 4, background: '#f7f7f9', fontSize: 11, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }
